import { EnvironmentsType } from '../global-types/feature-flags';
import { setEnabledEnvironments } from '../utils/utility/set-enabled-environments';

const getOptionalBooleanEnv = (value: string | undefined): boolean | undefined => {
  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return undefined;
};

const resolveOptionalOverrideFlag = (value: string | undefined, fallback: boolean): boolean => {
  return getOptionalBooleanEnv(value) ?? fallback;
};

export const useFeatureFlags = () => {
  /**
   * Flags
   */

  // Enables or disables using the url from 'image_url' for the image added to the card in the thumbnail component (MIMIR-3180)
  const IS_URL_IMAGE_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev, // Change Flag to Dev when working locally
    EnvironmentsType.CeDev,
  ]);

  // Enables and disables components related to H Plus
  const IS_H_PLUS_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  // Enables and disables components related to loyalty (added in MIMIR-3260)
  const IS_LOYALTY_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  // Enables text overlays as a whole, on all implementations
  const IS_TEXTBOX_OVERLAY_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  // Enables text overlays for S&S text zones
  const IS_SNS_TEXTBOX_OVERLAY_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  // Enables text overlays for POD user added text (inside of card)
  const IS_POD_USER_TEXTBOX_OVERLAY_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  // Enables text overlays for POD template defined text zones
  const IS_POD_TEXT_ZONE_OVERLAY_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  // Enables text overlays for POD edit mode
  const IS_POD_EDIT_MODE_OVERLAY_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  // Enables and disables the new Photo Drawer (MIMIR-3336)
  const photoDrawerEnabledFromEnvironment = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  const IS_PHOTO_DRAWER_ENABLED = resolveOptionalOverrideFlag(
    process.env.REACT_APP_OVERRIDE_PHOTO_DRAWER_ENABLED,
    photoDrawerEnabledFromEnvironment,
  );

  // Enables and disables address validation
  const IS_ADDRESS_VALIDATION_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  const IS_ADDRESS_VERIFICATION_ENHANCED_MATCH_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  // Enables chatbot feature
  const IS_CHATBOT_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  const IS_ADDRESS_BULK_UPLOAD_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  const IS_MULT_ADDRESS_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  const IS_ADOBE_ANALYTICS_ENABLED = setEnabledEnvironments([EnvironmentsType.UsDev, EnvironmentsType.UsProd]);

  const IS_ENVELOPE_PRINTING_OPTIONS_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  const IS_USER_ZONE_TRANSPARENT = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
    EnvironmentsType.UkDev,
    EnvironmentsType.UkStage,
    EnvironmentsType.UkProd,
  ]);

  // Feature flag to hide/show edit controls like scale and rotate
  const IS_FORCE_FILL_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  const IS_ADDRESS_AUTOCOMPLETE_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  const IS_ADDRESSBOOK_SORT_SELECT_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  const IS_ADDRESSBOOK_SELECTALL_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  const IS_ADDRESSBOOK_SEARCH_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  // Enables pagination for photo drawer
  const IS_DRAWER_PAGINATION_ENABLED = setEnabledEnvironments([]);

  // Enables canvas JSON preview generation instead of print JSON preview
  const CANVAS_JSON_PREVIEW = setEnabledEnvironments([]);

  // Enables split inside panel (left/right) for portrait POD cards
  const IS_SPLIT_INSIDE_PANEL_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  // Enables the Edit/Preview toggle in the EditorHeader center slot.
  // When off, the crown logo is shown instead.
  const IS_PREVIEW_TOGGLE_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  // Enables the new EditorHeader (MIMIR-3377), which includes the new layout and styles, and disables the legacy EditorHeader.
  const editorHeaderFromEnvironment = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);
  const IS_EDITOR_HEADER_ENABLED = resolveOptionalOverrideFlag(
    process.env.REACT_APP_OVERRIDE_EDITOR_HEADER_ENABLED,
    editorHeaderFromEnvironment,
  );

  // Enables the new image edit toolbar (zustand-driven) and disables the
  // legacy ImageEditDrawer.
  const newImageEditToolbarFromEnvironment = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);
  const IS_NEW_IMAGE_EDIT_TOOLBAR_ENABLED = resolveOptionalOverrideFlag(
    process.env.REACT_APP_OVERRIDE_NEW_IMAGE_EDIT_TOOLBAR_ENABLED,
    newImageEditToolbarFromEnvironment,
  );

  // Enables the new CardFaceToggle (ToggleGroup-based) navigation and disables
  // the legacy CardSelectBar (CarouselImageDotButton-based) navigation.
  const editorNavigationFromEnvironment = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);
  const IS_EDITOR_NAVIGATION_ENABLED = resolveOptionalOverrideFlag(
    process.env.REACT_APP_OVERRIDE_EDITOR_NAVIGATION_ENABLED,
    editorNavigationFromEnvironment,
  );

  // Keeps undo and redo controls opt-in while rollout is pending.
  const editorUndoRedoFromEnvironment = setEnabledEnvironments([]);
  const IS_EDITOR_UNDO_REDO_ENABLED = resolveOptionalOverrideFlag(
    process.env.REACT_APP_OVERRIDE_EDITOR_UNDO_REDO_ENABLED,
    editorUndoRedoFromEnvironment,
  );

  // Enables the new FontToolbar (ToolbarActionMenu-based) and disables the
  // legacy FontDrawer for text editing.
  const IS_NEW_FONT_TOOLBAR_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  // Enables smooth horizontal scrolling for the color palette in the mobile font drawer
  const IS_MOBILE_COLOR_SCROLL_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  // Enables the floating tool menu (FAB "+") in place of the toolbar on inside surfaces
  const floatingMenuEnabledFromEnvironment = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);
  const IS_FLOATING_MENU_ENABLED = resolveOptionalOverrideFlag(
    process.env.REACT_APP_OVERRIDE_FLOATING_MENU_ENABLED,
    floatingMenuEnabledFromEnvironment,
  );

  // Enables inline quantity+price dropdown in the header, replacing the IconButton + SummaryDrawer flow
  const IS_HEADER_QUANTITY_DROPDOWN_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  // Enables the redesigned behavior for the new PhotoDrawer across desktop and mobile.
  const photoDrawerRedesignFromEnvironment = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);
  const IS_PHOTO_DRAWER_REDESIGN_ENABLED = resolveOptionalOverrideFlag(
    process.env.REACT_APP_OVERRIDE_PHOTO_DRAWER_REDESIGN,
    photoDrawerRedesignFromEnvironment,
  );

  // Enables drag-and-drop of photos from the Photo Drawer/Tray onto the canvas.
  // Disabled in stage/prod because dropped images land on the top layer instead
  // of inside photo zones, which causes printline errors.
  const IS_PHOTO_TRAY_DRAG_TO_CANVAS_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  // Enables the display of the field display_name on address form across desktop and mobile.
  const IS_ADDRESS_DISPLAY_NAME_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);
  // Enables the dedicated Digital Greeting send flow.
  const IS_ECARD_SEND_ENABLED = setEnabledEnvironments([
    EnvironmentsType.UsDev,
    EnvironmentsType.UsStage,
    EnvironmentsType.UsProd,
  ]);

  return {
    IS_URL_IMAGE_ENABLED,
    IS_ENVELOPE_PRINTING_OPTIONS_ENABLED,
    IS_H_PLUS_ENABLED,
    IS_LOYALTY_ENABLED,
    IS_CHATBOT_ENABLED,
    IS_ADDRESS_VALIDATION_ENABLED,
    IS_ADDRESS_VERIFICATION_ENHANCED_MATCH_ENABLED,
    IS_ADDRESS_BULK_UPLOAD_ENABLED,
    IS_MULT_ADDRESS_ENABLED,
    IS_ADOBE_ANALYTICS_ENABLED,
    IS_USER_ZONE_TRANSPARENT,
    IS_TEXTBOX_OVERLAY_ENABLED,
    IS_SNS_TEXTBOX_OVERLAY_ENABLED,
    IS_POD_EDIT_MODE_OVERLAY_ENABLED,
    IS_POD_USER_TEXTBOX_OVERLAY_ENABLED,
    IS_POD_TEXT_ZONE_OVERLAY_ENABLED,
    IS_FORCE_FILL_ENABLED,
    IS_ADDRESS_AUTOCOMPLETE_ENABLED,
    IS_ADDRESSBOOK_SORT_SELECT_ENABLED,
    IS_ADDRESSBOOK_SEARCH_ENABLED,
    IS_ADDRESSBOOK_SELECTALL_ENABLED,
    IS_PHOTO_DRAWER_ENABLED,
    IS_DRAWER_PAGINATION_ENABLED,
    CANVAS_JSON_PREVIEW,
    IS_SPLIT_INSIDE_PANEL_ENABLED,
    IS_EDITOR_HEADER_ENABLED,
    IS_EDITOR_NAVIGATION_ENABLED,
    IS_EDITOR_UNDO_REDO_ENABLED,
    IS_PREVIEW_TOGGLE_ENABLED,
    IS_NEW_IMAGE_EDIT_TOOLBAR_ENABLED,
    IS_NEW_FONT_TOOLBAR_ENABLED,
    IS_MOBILE_COLOR_SCROLL_ENABLED,
    IS_FLOATING_MENU_ENABLED,
    IS_HEADER_QUANTITY_DROPDOWN_ENABLED,
    IS_PHOTO_DRAWER_REDESIGN_ENABLED,
    IS_PHOTO_TRAY_DRAG_TO_CANVAS_ENABLED,
    IS_ADDRESS_DISPLAY_NAME_ENABLED,
    IS_ECARD_SEND_ENABLED,
  };
};
